/**
 * KYCDashboardPage — `/kyc` index landing.
 *
 * Slice 2: Shows real tier-upgrade request counts by status from
 * GET /v1/kyc/tier-upgrade-requests/stats, plus recent SARs.
 * Errors render as inline ErrorBanner; no DeferredNotice usage remains.
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Clock, ShieldCheck, XCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { listSars, getTierUpgradeStats } from '../../lib/api/kyc';
import { PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

function ErrorBanner({ title, message }: { readonly title: string; readonly message: string }): JSX.Element {
  return (
    <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900 space-y-1">
      <p className="font-semibold">{title}</p>
      <p className="text-red-800/90">{message}</p>
    </div>
  );
}

export function KYCDashboardPage(): JSX.Element {
  const stats = useQuery({
    queryKey: ['kyc', 'tier-upgrade', 'stats'],
    queryFn: () => getTierUpgradeStats(),
    retry: false,
    staleTime: 30_000,
  });

  const sars = useQuery({
    queryKey: ['kyc', 'sars', 'dashboard'],
    queryFn: () => listSars({ pageSize: 10 }),
    retry: false,
    staleTime: 30_000,
  });

  const statsDenied =
    stats.isError &&
    stats.error instanceof ApiError &&
    (stats.error.status === 401 || stats.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Identity & KYC"
        description="Customer identity verification, tier-upgrade review queue, SAR pipeline."
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/kyc/verify"
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
            >
              Review queue
            </Link>
            <Link
              to="/kyc/compliance"
              className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Compliance
            </Link>
          </div>
        }
      />

      {/* Tier-upgrade KPI counts */}
      <div>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Tier-upgrade requests</h2>
        {statsDenied ? (
          <ErrorBanner
            title="Permission required"
            message="Token missing Kyc/readReview. Contact the platform admin to grant the role."
          />
        ) : (
          <div
            className="grid grid-cols-2 lg:grid-cols-4 gap-4"
            data-testid="kyc-stats"
          >
            <KPICard
              title="Pending review"
              value={stats.isPending ? '—' : String(stats.data?.pendingReview ?? 0)}
              hint="PENDING_REVIEW"
              icon={Clock}
              tone={
                stats.data && stats.data.pendingReview > 0 ? 'warning' : 'default'
              }
            />
            <KPICard
              title="Approved"
              value={stats.isPending ? '—' : String(stats.data?.approved ?? 0)}
              hint="APPROVED by operator"
              icon={CheckCircle}
              tone="success"
            />
            <KPICard
              title="Rejected"
              value={stats.isPending ? '—' : String(stats.data?.rejected ?? 0)}
              hint="REJECTED by operator"
              icon={XCircle}
              tone="danger"
            />
            <KPICard
              title="Auto-approved"
              value={stats.isPending ? '—' : String(stats.data?.autoApproved ?? 0)}
              hint="AUTO_APPROVED via NIMC"
              icon={ShieldCheck}
            />
          </div>
        )}
      </div>

      {/* Quick-action links */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link
          to="/kyc/verify"
          className="bg-white border border-border rounded-xl p-5 hover:shadow-sm transition-shadow"
          data-testid="kyc-review-queue-link"
        >
          <h3 className="font-semibold text-gray-900 text-sm mb-1">Review queue</h3>
          <p className="text-xs text-muted">Pending NIN tier-upgrade requests awaiting operator decision.</p>
        </Link>
        <Link
          to="/kyc/compliance"
          className="bg-white border border-border rounded-xl p-5 hover:shadow-sm transition-shadow"
        >
          <h3 className="font-semibold text-gray-900 text-sm mb-1">Compliance ledger</h3>
          <p className="text-xs text-muted">Audit trail of recent operator approve / reject decisions.</p>
        </Link>
        <Link
          to="/kyc/sar"
          className="bg-white border border-border rounded-xl p-5 hover:shadow-sm transition-shadow"
        >
          <h3 className="font-semibold text-gray-900 text-sm mb-1">SAR pipeline</h3>
          <p className="text-xs text-muted">Suspicious activity reports filed for NFIU goAML.</p>
        </Link>
      </div>

      {/* Recent SARs */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Recent SARs</h2>
        {sars.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : sars.isError ? (
          <ErrorBanner title="SAR queue unavailable" message={(sars.error as Error).message} />
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">SAR #</th>
                  <th className="text-left px-4 py-2">Trigger</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Filed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(sars.data?.items ?? []).map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{s.sarNumber}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">{s.trigger}</td>
                    <td className="px-4 py-2 text-xs">
                      <StatusBadge status={s.status} />
                    </td>
                    <td className="px-4 py-2 text-xs text-muted">
                      {s.filedAt ? new Date(s.filedAt).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(sars.data?.items.length ?? 0) === 0 ? (
              <p className="p-8 text-center text-sm text-muted">No SARs yet.</p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
